import React, { Fragment, useEffect, useRef, useState } from "react";

import {
  Alert,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Row,
  Spinner,
} from "reactstrap";

// hooks
import { useProfile } from "../../Components/Hooks/UserHooks";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { createPayAgreement } from "../../slices/apps/agreementSlice";
import { fetchCustomer } from "../../slices/apps/customerSlice";
import { findBin } from "../../slices/apps/paymentSlice";
import { fetchBulkPackageCount } from "../../slices/apps/packageSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react select
import Select from "react-select";

//Import Flatepicker
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";

// diğer importlar
import ReactInputMask from "react-input-mask";

// toaster
import { Slide, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// data dosyaları
import CityJson from "../../common/data/city.json";

// router
import { useNavigate } from "react-router-dom";

const city = CityJson.map((data) => ({
  label: data.il_adi,
  value: data.il_adi,
  districts: data.ilceler
    .map((ilce) => ({
      label: ilce.ilce_adi,
      value: ilce.ilce_adi,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
})).sort((a, b) => a.label.localeCompare(b.label));

import { carsInfo } from "../../common/data/cars";

// regex, değişkenler
const phoneRegExp =
  /^(?:[1-9][0-9]{2} [0-9]{3} [0-9]{2} [0-9]{2}|[1-9][0-9]{9})$/;

const Checkout = ({
  bulkSaleId,
  preselectedCustomerId,
  isRenewal,
  carInfo,
  renewalCustomerData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile } = useProfile();

  // states
  const {
    packageState,
    createPayAgreementLoading,
    bulkPackageCount,
    customers,
    cardInfo,
    agencyEarningsSummary,
  } = useSelector((state) => {
    return {
      packageState: state.package.package,
      createPayAgreementLoading: state.agreement.createPayAgreementLoading,
      bulkPackageCount: state.package.bulkPackageCount,
      customers: state.customer.customers,
      cardInfo: state.payment.cardInfo,
      agencyEarningsSummary: state.agency.agencyEarningsSummary,
    };
  }, shallowEqual);

  const [priceModificationMode, setPriceModificationMode] = useState("normal");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [increaseAmount, setIncreaseAmount] = useState("");
  const [finalPrice, setFinalPrice] = useState(packageState.Price || 0);
  const renewalDataFilled = useRef(false);
  const [renewalFormLoading, setRenewalFormLoading] = useState(
    isRenewal && preselectedCustomerId ? true : false,
  );

  const showCommissionFeatures =
    agencyEarningsSummary && Object.keys(agencyEarningsSummary).length > 0;

  const commissionRate =
    agencyEarningsSummary?.CommissionRate ?? packageState.ProfitMargin ?? 0;

  const packageRate = agencyEarningsSummary?.PackageRate ?? null;

  const walletBalance = agencyEarningsSummary?.TotalEarning || 0;

  useEffect(() => {
    let calculatedPrice = packageState.Price;
    if (priceModificationMode === "decrease") {
      const effectiveDiscountRate = (discountPercentage * commissionRate) / 100;

      calculatedPrice = packageState.Price * (1 - effectiveDiscountRate / 100);
    } else if (priceModificationMode === "increase") {
      const amount = parseFloat(increaseAmount) || 0;
      calculatedPrice = packageState.Price + amount;
    }
    setFinalPrice(calculatedPrice);
    validation.setFieldValue("price", calculatedPrice);
  }, [
    priceModificationMode,
    discountPercentage,
    increaseAmount,
    packageState.Price,
    commissionRate,
  ]);

  const fetchData = async () => {
    try {
      await dispatch(fetchBulkPackageCount(bulkSaleId));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (
      packageState?.IsBulkSale &&
      packageState?.Id &&
      userProfile?.data?.AgencyID
    ) {
      fetchData();
    }
  }, []);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      packageId: packageState.Id,
      price: packageState.Price || 0,
      issuedTime: "",
      customerName: "",
      phoneNumber: "",
      email: "",
      isActive: true,
      taxNumber: "",
      taxOffice: "",
      city: "",
      district: "",
      address: "",
      customerId: 0,

      plate: "",
      usageType: "",
      modelYear: "",
      brand: "",
      model: "",

      paymentType: packageState.IsBulkSale ? 2 : 0,
      cardHolderName: "",
      cardNumber: "",
      expiryDate: "",
      cvc: "",
    },

    validationSchema: Yup.object({
      issuedTime: Yup.string().required("Lütfen başlangıç tarihi girin"),
      customerName: Yup.string().required("Lütfen müşteri bilgisi girin"),

      phoneNumber: Yup.string()
        .required("Lütfen telefon numarası girin")
        .matches(phoneRegExp, "Lütfen geçerli bir telefon numarası girin"),
      email: Yup.string()
        .nullable()
        .email("Lütfen geçerli bir e-posta adresi girin")
        .notRequired(),
      taxNumber: Yup.string()
        .nullable()
        .notRequired()
        .test("is-valid-taxnumber", "VKN/TCKN geçersiz", (value) => {
          if (!value || value.trim() === "") return true;
          return /^[0-9]+$/.test(value);
        })
        .min(10, "VKN/TCKN en az 10 karakter olmalıdır")
        .max(11, "VKN/TCKN en fazla 11 karakter olmalıdır"),
      taxOffice: Yup.string(),
      city: Yup.string(),
      district: Yup.string(),
      address: Yup.string(),

      plate: Yup.string().required("Lütfen plaka giriniz"),
      usageType: Yup.string(),
      modelYear: Yup.number()
        .typeError("Model yılı girin")
        .required("Model yılı girin")
        .max(new Date().getFullYear(), `Geçersiz model yılı`)
        .test(
          "min-year-check",
          `Araç en fazla ${packageState?.CarYearLimit} yaşında olmalı`,

          function (value) {
            if (!packageState?.IsCarYearLimitEnabled) return true;
            return (
              value >= new Date().getFullYear() - packageState?.CarYearLimit
            );
          },
        ),
      brand: Yup.string(),
      model: Yup.string(),

      cardHolderName: Yup.string().test(
        "card-required",
        "Lütfen ad soyad girin",
        function (value) {
          return this.parent.paymentType != 0 || !!value;
        },
      ),
      cardNumber: Yup.string()
        .matches(
          /^[0-9]{4} [0-9]{4} [0-9]{4} [0-9]{4}$/,
          "Geçersiz kart numarası",
        )
        .test("card-required", "Lütfen kart numarası girin", function (value) {
          return this.parent.paymentType != 0 || !!value;
        }),
      expiryDate: Yup.string()
        .matches(
          /^(0[1-9]|1[0-2])\/([0-9]{2})$/,
          "Geçersiz tarih formatı (AA/YY)",
        )
        .test(
          "expiry-required",
          "Lütfen son kullanma tarihini girin",
          function (value) {
            return this.parent.paymentType != 0 || !!value;
          },
        ),
      cvc: Yup.string()
        .matches(/^[0-9]{3}$/, "Geçersiz CVV")
        .test("cvc-required", "Lütfen CVV girin", function (value) {
          return this.parent.paymentType != 0 || !!value;
        }),
    }),

    onSubmit: async (values) => {
      const { cardHolderName, cardNumber, expiryDate, cvc } = values;
      const [expMonth, expYear] = expiryDate ? expiryDate.split("/") : ["", ""];
      const cardNumberEnd = cardNumber ? cardNumber.replaceAll(" ", "") : "";

      let calculatedDiscountRate = 0;
      let calculatedIncreaseAmount = 0;
      let adjustmentType = 0;

      if (priceModificationMode === "decrease") {
        adjustmentType = 1;
        calculatedDiscountRate = (discountPercentage * commissionRate) / 100;
      } else if (priceModificationMode === "increase") {
        adjustmentType = 2;
        calculatedIncreaseAmount = parseFloat(increaseAmount) || 0;
      }

      const paymentData = {
        ...values,
        price: values.price,
        cardHolderName,
        cardNumber: cardNumberEnd,
        expMonth: parseInt(expMonth, 10) || null,
        expYear: parseInt(expYear, 10) || null,
        cvc,
        installmentNumber: 1,
        description: "",
        posId: cardInfo.PosId || null,
        isSecure: cardInfo.IsSecure || false,
        agencyId: userProfile?.data?.AgencyID,
        bulkSaleId: packageState.IsBulkSale ? bulkSaleId : null,
        issuedTime: values.issuedTime
          ? (() => {
              const date = new Date(values.issuedTime);
              date.setHours(0, 0, 0, 0);
              date.setHours(date.getHours() + 3);
              return date.toISOString();
            })()
          : null,

        discountRate: calculatedDiscountRate,
        increaseAmount: calculatedIncreaseAmount,
        priceAdjustmentType: adjustmentType,
      };

      try {
        const resultAction = await dispatch(createPayAgreement(paymentData));

        if (resultAction.meta.requestStatus === "fulfilled") {
          navigate("/agreements");
        } else if (resultAction.meta.requestStatus === "rejected") {
          try {
            const result = JSON.parse(resultAction.error.message);
            if (result.Code == 600) {
              navigate("/agreements");
            }
          } catch (e) {
            console.error("Error parsing response", e);
          }
        }
      } catch (error) {
        console.error("Failed:", error);
      }
    },
  });

  const fetchCustomerFunc = async (customerId) => {
    try {
      const resultAction = await dispatch(fetchCustomer(customerId));

      if (resultAction.meta.requestStatus === "fulfilled") {
        validation.setValues({
          ...validation.values,
          customerName: resultAction.payload?.CustomerName || "",
          phoneNumber: resultAction.payload?.PhoneNumber || "",
          email: resultAction.payload?.Email || "",
          taxNumber: resultAction.payload?.TaxNumber || "",
          taxOffice: resultAction.payload?.TaxOffice || "",
          city: resultAction.payload?.City || "Adana",
          district: resultAction.payload?.District || "",
          address: resultAction.payload?.Address || "",
          customerId: resultAction.payload?.Id,
        });
      }
    } catch (error) {
      console.error("Failed:", error);
    }
  };

  const handleCustomerChange = (value) => {
    const customerId = value ? value.value : 0;
    validation.setFieldValue("customerId", customerId);

    if (customerId !== 0) {
      fetchCustomerFunc(customerId);
    } else {
      validation.setValues({
        ...validation.initialValues,
      });
    }
  };

  useEffect(() => {
    const cardNumber = validation.values.cardNumber.replaceAll(" ", "");

    if (cardNumber.length === 6 || cardNumber.length === 16) {
      try {
        dispatch(findBin(cardNumber.slice(0, 6)));
      } catch (error) {
        console.error("Failed:", error);
      }
    }
  }, [validation.values.cardNumber]);

  useEffect(() => {
    if (!validation.values.issuedTime) {
      const today = new Date();

      validation.setFieldValue("issuedTime", today.toISOString());
    }
  }, []);

  // Uzatma senaryosunda: Payment'tan gelen müşteri ve araç bilgilerini doldur
  useEffect(() => {
    // Zaten doldurulduysa çık
    if (renewalDataFilled.current) return;

    // Uzatma senaryosu değilse çık
    if (!isRenewal || !preselectedCustomerId || !renewalCustomerData) return;

    // REF'i hemen set et - race condition'ı önle
    renewalDataFilled.current = true;

    // Bugünün tarihini al
    const today = new Date();

    // Müşteri bilgilerini form'a doldur
    validation.setValues({
      ...validation.values,
      // Sözleşme tarihini set et (zorunlu alan)
      issuedTime: validation.values.issuedTime || today.toISOString(),
      customerId: preselectedCustomerId,
      customerName: renewalCustomerData.CustomerName || "",
      phoneNumber: renewalCustomerData.PhoneNumber || "",
      email: renewalCustomerData.Email || "",
      taxNumber: renewalCustomerData.TaxNumber || "",
      taxOffice: renewalCustomerData.TaxOffice || "",
      city: renewalCustomerData.City || "Adana",
      district: renewalCustomerData.District || "",
      address: renewalCustomerData.Address || "",
      // Araç bilgilerini de aynı anda set et
      plate: carInfo?.plate || "",
      brand: carInfo?.brand || "",
      model: carInfo?.model || "",
      modelYear: carInfo?.modelYear || "",
      usageType: carInfo?.usageType || "",
    });

    // Form dolduruldu, loading'i kapat
    setRenewalFormLoading(false);
  }, [isRenewal, preselectedCustomerId, renewalCustomerData, carInfo]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const basePrice = packageState.Price || 0;
  let modificationAmount = 0;
  let modificationLabel = "İndirim / Artırım";

  if (priceModificationMode === "decrease") {
    const effectiveDiscountRate = (discountPercentage * commissionRate) / 100;
    modificationAmount = -1 * (basePrice * (effectiveDiscountRate / 100));

    modificationLabel = "İndirim Tutarı";
  } else if (priceModificationMode === "increase") {
    modificationAmount = parseFloat(increaseAmount) || 0;
    modificationLabel = "Fiyat Artırımı";
  }

  const baseEarnings = basePrice * (commissionRate / 100);

  let totalEarnings = 0;

  if (priceModificationMode === "decrease") {
    const costPrice = basePrice * (1 - commissionRate / 100);
    totalEarnings = finalPrice - costPrice;
  } else if (priceModificationMode === "increase") {
    const increaseAmountVal = parseFloat(increaseAmount) || 0;
    const increaseRate =
      packageRate !== null && packageRate !== undefined ? packageRate : 50;

    let extraEarnings = increaseAmountVal * (increaseRate / 100);

    if (userProfile?.data?.Role === "SubAgency") {
      extraEarnings = extraEarnings * 0.8;
    }

    totalEarnings = baseEarnings + extraEarnings;
  } else {
    totalEarnings = baseEarnings;
  }

  const isBalancePayment = validation.values.paymentType == 3;

  const isInsufficientBalance = isBalancePayment && finalPrice > walletBalance;

  // Uzatma senaryosunda form dolana kadar loading göster
  if (renewalFormLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-3 text-muted">
            Müşteri ve araç bilgileri yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <Row>
        <Col lg={7}>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
            action="#"
            autoComplete="off"
          >
            <Card>
              <CardBody>
                <h4 className="mt-4">Müşteri Bilgileri</h4>
                <hr className="mb-4 text-muted" />

                <Row className="align-items-start">
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="customerIdSelect" className="form-label">
                        Müşteri
                      </Label>
                      <Select
                        isClearable
                        isDisabled={isRenewal && preselectedCustomerId}
                        placeholder="Müşteri seçiniz"
                        id="customerIdSelect"
                        name="customerId"
                        options={customers?.map((item) => ({
                          label: item.customerName,
                          value: item.id,
                        }))}
                        noOptionsMessage={() => "Seçenek bulunamadı"}
                        value={
                          customers
                            ?.map((item) => ({
                              label: item.customerName,
                              value: item.id,
                            }))
                            .find(
                              (item) =>
                                item.value === validation.values.customerId,
                            ) || { label: "Müşteri seçiniz", value: 0 }
                        }
                        onChange={handleCustomerChange}
                        onBlur={validation.handleBlur}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor:
                              validation.touched.customerId &&
                              validation.errors.customerId &&
                              !state.isFocused
                                ? "#e30a17"
                                : "#e9ebec",
                          }),
                        }}
                      />
                      {validation.touched.customerId &&
                        validation.errors.customerId && (
                          <div className="d-block invalid-feedback">
                            {validation.errors.customerId}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">Sözleşme Tarihi</Label>
                      <Flatpickr
                        className={
                          validation.touched.issuedTime &&
                          validation.errors.issuedTime
                            ? "form-control is-invalid"
                            : "form-control"
                        }
                        options={{
                          dateFormat: "d.m.Y",
                          minDate: "today",
                          maxDate: new Date().fp_incr(14),
                          locale: { ...Turkish },
                        }}
                        value={
                          validation.values.issuedTime
                            ? new Date(validation.values.issuedTime)
                            : new Date()
                        }
                        onChange={(date) => {
                          validation.setFieldValue(
                            "issuedTime",
                            date[0].toISOString(),
                          );
                        }}
                      />
                      {validation.touched.issuedTime &&
                        validation.errors.issuedTime && (
                          <div
                            type="invalid"
                            className="d-block invalid-feedback"
                          >
                            {validation.errors.issuedTime}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="customerName" className="form-label">
                        Ad Soyad / Firma Ünvanı
                      </Label>
                      <Input
                        disabled={validation.values.customerId != 0}
                        name="customerName"
                        className="form-control"
                        placeholder="Ad Soyad / Firma Ünvanı"
                        type="text"
                        autoComplete="off"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.customerName || ""}
                        invalid={
                          validation.touched.customerName &&
                          validation.errors.customerName
                            ? true
                            : false
                        }
                      />
                      {validation.touched.customerName &&
                        validation.errors.customerName && (
                          <FormFeedback type="invalid">
                            {validation.errors.customerName}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="phoneNumber">
                        Telefon Numarası
                      </Label>
                      <InputGroup>
                        <span
                          className="input-group-text "
                          style={{
                            width: "44px",
                            justifyContent: "center",
                          }}
                          id="inputGroupPrepend2"
                        >
                          0
                        </span>
                        <ReactInputMask
                          disabled={validation.values.customerId != 0}
                          mask="999 999 99 99"
                          maskChar=" "
                          name="phoneNumber"
                          placeholder="Telefon Numarası"
                          style={{ zIndex: 0 }}
                          className={
                            validation.touched.phoneNumber &&
                            validation.errors.phoneNumber
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          onChange={validation.handleChange}
                          value={validation.values.phoneNumber || ""}
                        />
                      </InputGroup>
                      {validation.touched.phoneNumber &&
                        validation.errors.phoneNumber && (
                          <div
                            type="invalid"
                            className="d-block invalid-feedback"
                          >
                            {validation.errors.phoneNumber}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="email" className="form-label">
                        E-posta Adresi
                      </Label>
                      <Input
                        disabled={validation.values.customerId != 0}
                        name="email"
                        className="form-control"
                        placeholder="Kullanıcı E-posta Adresi"
                        type="text"
                        autoComplete="off"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={
                          validation.touched.email && validation.errors.email
                            ? true
                            : false
                        }
                      />
                      {validation.touched.email && validation.errors.email && (
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="taxNumber">
                        VKN/TCKN
                      </Label>
                      <InputGroup>
                        <input
                          name="taxNumber"
                          placeholder="VKN/TCKN"
                          inputMode="numeric"
                          type="text"
                          autoComplete="off"
                          maxLength={11}
                          className={
                            validation.touched.taxNumber &&
                            validation.errors.taxNumber
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");

                            validation.setFieldValue("taxNumber", value);
                          }}
                          value={validation.values.taxNumber || ""}
                        />
                      </InputGroup>
                      {validation.touched.taxNumber &&
                        validation.errors.taxNumber && (
                          <div
                            type="invalid"
                            className="d-block invalid-feedback"
                          >
                            {validation.errors.taxNumber}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="taxOffice" className="form-label">
                        Vergi Dairesi
                      </Label>
                      <Input
                        disabled={validation.values.customerId != 0}
                        name="taxOffice"
                        className="form-control"
                        placeholder="Vergi Dairesi"
                        type="text"
                        autoComplete="off"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.taxOffice || ""}
                        invalid={
                          validation.touched.taxOffice &&
                          validation.errors.taxOffice
                            ? true
                            : false
                        }
                      />
                      {validation.touched.taxOffice &&
                        validation.errors.taxOffice && (
                          <FormFeedback type="invalid">
                            {validation.errors.taxOffice}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="citySelect" className="form-label">
                        İl
                      </Label>
                      <Select
                        isDisabled={validation.values.customerId != 0}
                        placeholder="İl seçiniz"
                        id="citySelect"
                        name="city"
                        options={city}
                        noOptionsMessage={() => "Seçenek bulunamadı"}
                        isClearable={true}
                        value={city.find(
                          (item) => item.value === validation.values.city,
                        )}
                        onChange={(value) => {
                          validation.setFieldValue(
                            "city",
                            value ? value.value : "",
                          );
                          validation.setFieldValue("district", "");
                        }}
                        onBlur={validation.handleBlur}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor:
                              validation.touched.city &&
                              validation.errors.city &&
                              !state.isFocused
                                ? "#e30a17"
                                : "#e9ebec",
                          }),
                        }}
                      />
                      {validation.touched.city && validation.errors.city && (
                        <div className="d-block invalid-feedback">
                          {validation.errors.city}
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="districtSelect" className="form-label">
                        İlçe
                      </Label>
                      <Select
                        isDisabled={
                          validation.values.customerId != 0 ||
                          !validation.values.city
                        }
                        placeholder="İlçe seçiniz"
                        id="districtSelect"
                        name="district"
                        options={
                          city.find(
                            (item) => item.value === validation.values.city,
                          )?.districts || []
                        }
                        noOptionsMessage={() => "Seçenek bulunamadı"}
                        isClearable={true}
                        value={
                          city
                            .find(
                              (item) => item.value === validation.values.city,
                            )
                            ?.districts.find(
                              (district) =>
                                district.value === validation.values.district,
                            ) || ""
                        }
                        onChange={(value) => {
                          validation.setFieldValue(
                            "district",
                            value ? value.value : "",
                          );
                        }}
                        onBlur={validation.handleBlur}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor:
                              validation.touched.district &&
                              validation.errors.district &&
                              !state.isFocused
                                ? "#e30a17"
                                : "#e9ebec",
                          }),
                        }}
                      />
                      {validation.touched.district &&
                        validation.errors.district && (
                          <div className="d-block invalid-feedback">
                            {validation.errors.district}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="address" className="form-label">
                        Adres
                      </Label>
                      <Input
                        disabled={validation.values.customerId != 0}
                        name="address"
                        className="form-control"
                        placeholder="Mahalle, cadde, sokak..."
                        type="textarea"
                        autoComplete="off"
                        rows="2"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.address || ""}
                        invalid={
                          validation.touched.address &&
                          validation.errors.address
                            ? true
                            : false
                        }
                        style={{ resize: "none" }}
                      />
                      {validation.touched.address &&
                        validation.errors.address && (
                          <FormFeedback type="invalid">
                            {validation.errors.address}
                          </FormFeedback>
                        )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h4 className="mt-4">Araç Bilgileri</h4>
                <hr className="mb-4 text-muted" />

                <Row className="align-items-start">
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="plate" className="form-label">
                        Plaka
                      </Label>
                      <Input
                        name="plate"
                        className="form-control"
                        placeholder="Plaka"
                        type="text"
                        autoComplete="off"
                        maxLength={50}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          validation.setFieldValue("plate", value);
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.plate || ""}
                        invalid={
                          validation.touched.plate && validation.errors.plate
                            ? true
                            : false
                        }
                      />
                      {validation.touched.plate && validation.errors.plate && (
                        <FormFeedback type="invalid">
                          {validation.errors.plate}
                        </FormFeedback>
                      )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="usageTypeSelect" className="form-label">
                        Araç Tipi
                      </Label>
                      <Select
                        placeholder="Araç tipi seçiniz"
                        id="usageTypeSelect"
                        name="usageType"
                        options={carsInfo.map((item) => ({
                          value: item.typeName,
                          label: item.typeName,
                        }))}
                        noOptionsMessage={() => "Seçenek bulunamadı"}
                        isClearable={true}
                        value={carsInfo
                          .map((item) => ({
                            value: item.typeName,
                            label: item.typeName,
                          }))
                          .find(
                            (option) =>
                              option.value === validation.values.usageType,
                          )}
                        onChange={(value) => {
                          validation.setFieldValue(
                            "usageType",
                            value ? value.value : "",
                          );
                          validation.setFieldValue("brand", "");
                        }}
                        onBlur={validation.handleBlur}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor:
                              validation.touched.usageType &&
                              validation.errors.usageType &&
                              !state.isFocused
                                ? "#e30a17"
                                : "#e9ebec",
                          }),
                        }}
                      />
                      {validation.touched.usageType &&
                        validation.errors.usageType && (
                          <div className="d-block invalid-feedback">
                            {validation.errors.usageType}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="modelYear">
                        Model Yılı
                      </Label>
                      <InputGroup>
                        <ReactInputMask
                          mask="9999"
                          maskChar=" "
                          name="modelYear"
                          placeholder="Model Yılı"
                          inputMode="numeric"
                          className={
                            validation.touched.modelYear &&
                            validation.errors.modelYear
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          onChange={validation.handleChange}
                          value={validation.values.modelYear}
                          style={{ zIndex: 0 }}
                        />
                      </InputGroup>
                      {validation.touched.modelYear &&
                        validation.errors.modelYear && (
                          <div
                            type="invalid"
                            className="d-block invalid-feedback"
                          >
                            {validation.errors.modelYear}
                          </div>
                        )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="brandSelect" className="form-label">
                        Marka
                      </Label>
                      <Select
                        placeholder="Marka seçiniz"
                        id="brandSelect"
                        name="brand"
                        options={
                          carsInfo
                            .find(
                              (item) =>
                                item.typeName === validation.values.usageType,
                            )
                            ?.brands.map((brand) => ({
                              value: brand.brandName,
                              label: brand.brandName,
                            })) || []
                        }
                        noOptionsMessage={() => "Seçenek bulunamadı"}
                        isClearable={true}
                        value={
                          carsInfo
                            .find(
                              (item) =>
                                item.typeName === validation.values.usageType,
                            )
                            ?.brands.map((brand) => ({
                              value: brand.brandName,
                              label: brand.brandName,
                            }))
                            .find(
                              (option) =>
                                option.value === validation.values.brand,
                            ) || ""
                        }
                        onChange={(value) => {
                          validation.setFieldValue(
                            "brand",
                            value ? value.value : "",
                          );
                        }}
                        onBlur={validation.handleBlur}
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor:
                              validation.touched.brand &&
                              validation.errors.brand &&
                              !state.isFocused
                                ? "#e30a17"
                                : "#e9ebec",
                          }),
                        }}
                      />
                      {validation.touched.brand && validation.errors.brand && (
                        <div className="d-block invalid-feedback">
                          {validation.errors.brand}
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="model" className="form-label">
                        Model
                      </Label>
                      <Input
                        name="model"
                        className="form-control"
                        placeholder="Model"
                        type="text"
                        autoComplete="off"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.model || ""}
                        invalid={
                          validation.touched.model && validation.errors.model
                            ? true
                            : false
                        }
                      />
                      {validation.touched.model && validation.errors.model && (
                        <FormFeedback type="invalid">
                          {validation.errors.model}
                        </FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h4 className="mt-4">Ödeme</h4>
                <hr className="mb-4 text-muted" />
                {packageState.IsBulkSale ? (
                  <Alert
                    color="success"
                    className="alert-border-left material-shadow"
                  >
                    <i className="ri-alert-line me-3 align-middle fs-16"></i>
                    Bu paket, <strong>ücretsiz</strong> olarak tanımlanacaktır!
                  </Alert>
                ) : (
                  <div>
                    <Row className="g-4 mb-4">
                      <Col sm={6} lg={4}>
                        <div>
                          <div className="form-check card-radio">
                            <Input
                              id="paymentMethod01"
                              name="paymentType"
                              type="radio"
                              className="form-check-input"
                              value={0}
                              checked={validation.values.paymentType == 0}
                              onChange={(e) => {
                                validation.handleChange(e);

                                validation.setFieldValue("cardHolderName", "");
                                validation.setFieldValue("cardNumber", "");
                                validation.setFieldValue("expiryDate", "");
                                validation.setFieldValue("cvc", "");
                              }}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="paymentMethod01"
                            >
                              <span className="fs-16 text-muted me-2">
                                <i className="ri-bank-card-fill align-bottom"></i>
                              </span>
                              <span className="fs-12 text-truncate">
                                Kredi / Banka Kartı
                              </span>
                            </Label>
                          </div>
                        </div>
                      </Col>
                      <Col sm={6} lg={4}>
                        <div>
                          <div className="form-check card-radio">
                            <Input
                              id="paymentMethod02"
                              name="paymentType"
                              type="radio"
                              className="form-check-input"
                              value={1}
                              checked={validation.values.paymentType == 1}
                              onChange={(e) => {
                                validation.handleChange(e);

                                validation.setFieldValue("cardHolderName", "");
                                validation.setFieldValue("cardNumber", "");
                                validation.setFieldValue("expiryDate", "");
                                validation.setFieldValue("cvc", "");
                              }}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="paymentMethod02"
                            >
                              <span className="fs-16 text-muted me-2">
                                <i className="ri-send-plane-line align-bottom"></i>
                              </span>
                              <span className="fs-12 text-truncate">
                                Havale / EFT
                              </span>
                            </Label>
                          </div>
                        </div>
                      </Col>
                      {userProfile?.data?.Role === "Agency" && (
                        <Col sm={6} lg={4}>
                          <div>
                            <div className="form-check card-radio">
                              <Input
                                id="paymentMethod03"
                                name="paymentType"
                                type="radio"
                                className="form-check-input"
                                value={3}
                                checked={validation.values.paymentType == 3}
                                onChange={(e) => {
                                  validation.handleChange(e);

                                  validation.setFieldValue(
                                    "cardHolderName",
                                    "",
                                  );
                                  validation.setFieldValue("cardNumber", "");
                                  validation.setFieldValue("expiryDate", "");
                                  validation.setFieldValue("cvc", "");
                                }}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="paymentMethod03"
                              >
                                <span className="fs-16 text-muted me-2">
                                  <i className="ri-wallet-3-fill align-bottom"></i>
                                </span>
                                <span className="fs-12 text-truncate">
                                  Bakiye
                                </span>
                              </Label>
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>

                    {isInsufficientBalance && (
                      <Alert
                        color="danger"
                        className="alert-border-left material-shadow"
                      >
                        <i className="ri-error-warning-line me-3 align-middle fs-16"></i>
                        <strong>Yetersiz Bakiye!</strong> Paketi satın almak
                        için hesabınızda yeterli bakiye bulunmamaktadır.
                      </Alert>
                    )}

                    {validation.values.paymentType == 0 && (
                      <Row className="g-2 mb-2">
                        <Col lg={12}>
                          <div>
                            <Label
                              htmlFor="cardHolderName"
                              className="form-label"
                            >
                              Ad Soyad
                            </Label>
                            <Input
                              id="cardHolderName"
                              name="cardHolderName"
                              value={validation.values.cardHolderName}
                              type="text"
                              className="form-control pe-5"
                              placeholder="Kart Sahibi Adı Soyadı"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.cardHolderName &&
                                validation.errors.cardHolderName
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.cardHolderName &&
                              validation.errors.cardHolderName && (
                                <FormFeedback type="invalid">
                                  {validation.errors.cardHolderName}
                                </FormFeedback>
                              )}
                          </div>
                        </Col>

                        <Col lg={12}>
                          <div>
                            <Label className="form-label" htmlFor="cardNumber">
                              Kart Numarası
                            </Label>
                            <InputGroup>
                              <ReactInputMask
                                mask="9999 9999 9999 9999"
                                maskChar=""
                                name="cardNumber"
                                alwaysShowMask={false}
                                inputMode="numeric"
                                type="tel"
                                className={
                                  validation.touched.cardNumber &&
                                  validation.errors.cardNumber
                                    ? "form-control is-invalid"
                                    : "form-control"
                                }
                                placeholder="Kart Numarası"
                                onChange={validation.handleChange}
                                value={validation.values.cardNumber}
                                style={{ zIndex: 0 }}
                              />
                            </InputGroup>
                            {validation.touched.cardNumber &&
                              validation.errors.cardNumber && (
                                <div
                                  type="invalid"
                                  className="d-block invalid-feedback"
                                >
                                  {validation.errors.cardNumber}
                                </div>
                              )}
                          </div>
                        </Col>

                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label" htmlFor="expiryDate">
                              Son Kullanma Tarihi
                            </Label>
                            <ReactInputMask
                              mask="99/99"
                              maskChar=" "
                              name="expiryDate"
                              inputMode="numeric"
                              className={
                                validation.touched.expiryDate &&
                                validation.errors.expiryDate
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              placeholder="AA/YY"
                              onChange={validation.handleChange}
                              value={validation.values.expiryDate}
                              style={{ zIndex: 0 }}
                            />
                            {validation.touched.expiryDate &&
                              validation.errors.expiryDate && (
                                <div
                                  type="invalid"
                                  className="d-block invalid-feedback"
                                >
                                  {validation.errors.expiryDate}
                                </div>
                              )}
                          </div>
                        </Col>

                        <Col lg={6}>
                          <div className="mb-3">
                            <Label className="form-label" htmlFor="cvc">
                              CVV
                            </Label>
                            <Input
                              id="cvc"
                              name="cvc"
                              inputMode="numeric"
                              maxLength="3"
                              className={
                                validation.touched.cvc && validation.errors.cvc
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                              placeholder="CVV"
                              onChange={validation.handleChange}
                              value={validation.values.cvc}
                            />
                            {validation.touched.cvc &&
                              validation.errors.cvc && (
                                <div
                                  type="invalid"
                                  className="d-block invalid-feedback"
                                >
                                  {validation.errors.cvc}
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>
                    )}
                    {validation.values.paymentType == 1 && (
                      <div className="sticky-side-div mb-4 mt-2">
                        <div className="table-responsive">
                          <table className="table mb-0">
                            <tbody>
                              <tr>
                                <th>
                                  <div className="d-flex flex-column">
                                    <span>BANKA</span>
                                  </div>
                                </th>
                                <td className="text-end">
                                  <span className="fw-semibold" id="bank">
                                    Türkiye İş Bankası
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th>
                                  <div className="d-flex flex-column">
                                    <span>AD SOYAD</span>
                                  </div>
                                </th>
                                <td className="text-end">
                                  <span
                                    className="fw-semibold"
                                    id="nameSurname"
                                  >
                                    GLOBAL TAŞIT YOL YARDIM LİMİTED ŞİRKETİ
                                  </span>
                                </td>
                              </tr>
                              <tr>
                                <th>
                                  <div className="d-flex flex-column">
                                    <span>IBAN</span>
                                  </div>
                                </th>
                                <td className="text-end d-flex justify-content-end align-items-center">
                                  <span className="fw-semibold" id="iban">
                                    TR47 0006 4000 0015 3140 2369 06
                                  </span>
                                  <div
                                    onClick={() => {
                                      const ibanText =
                                        "TR47 0006 4000 0015 3140 2369 06";
                                      const textArea =
                                        document.createElement("textarea");
                                      textArea.value = ibanText;
                                      document.body.appendChild(textArea);
                                      textArea.select();
                                      document.execCommand("copy");
                                      document.body.removeChild(textArea);

                                      toast.success("IBAN Kopyalandı", {
                                        position: "top-center",
                                        isLoading: false,
                                        autoClose: 5000,
                                        transition: Slide,
                                        hideProgressBar: true,
                                      });
                                    }}
                                    style={{
                                      cursor: "pointer",
                                      marginLeft: "0.3rem",
                                    }}
                                  >
                                    <i className="ri-file-copy-line align-middle me-2 fs-14 text-dark"></i>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {!isInsufficientBalance &&
                      userProfile?.data?.Role === "Agency" &&
                      validation.values.paymentType == 3 && (
                        <div className="sticky-side-div mb-4 mt-2">
                          <div className="table-responsive">
                            <table className="table mb-0">
                              <tbody>
                                <tr>
                                  <th>
                                    <div className="d-flex flex-column">
                                      <span>Hesap Bakiyesi:</span>
                                    </div>
                                  </th>
                                  <td className="text-end">
                                    <span className="fw-semibold" id="bank">
                                      {formatCurrency(walletBalance)} TL
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <th>
                                    <div className="d-flex flex-column">
                                      <span>Paket Tutarı:</span>
                                    </div>
                                  </th>
                                  <td className="text-end">
                                    <span
                                      className="fw-semibold"
                                      id="finalPrice"
                                    >
                                      {formatCurrency(finalPrice)} TL
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <th>
                                    <div className="d-flex flex-column">
                                      <span>Kalan Bakiye:</span>
                                    </div>
                                  </th>
                                  <td className="text-end d-flex justify-content-end align-items-center">
                                    <span className="fw-semibold" id="iban">
                                      {formatCurrency(
                                        walletBalance - finalPrice,
                                      )}{" "}
                                      TL
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                  </div>
                )}

                <div className="hstack gap-2 justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      validation.handleSubmit();
                    }}
                    disabled={
                      createPayAgreementLoading || isInsufficientBalance
                    }
                  >
                    Satın Al
                  </button>
                </div>
              </CardBody>
            </Card>
          </Form>
        </Col>
        <Col lg={5}>
          {!packageState.IsBulkSale &&
            userProfile?.data?.Role != "SuperAdmin" &&
            agencyEarningsSummary && (
              <Card>
                <CardBody>
                  <h4 className="mt-4">Fiyat Ayarlama</h4>
                  <hr className="mb-4 text-muted" />

                  <div className="d-flex flex-wrap align-items-center gap-4">
                    <FormGroup check style={{ width: "105px" }}>
                      <Input
                        name="priceModification"
                        type="radio"
                        id="priceNormal"
                        value="normal"
                        checked={priceModificationMode === "normal"}
                        onChange={(e) =>
                          setPriceModificationMode(e.target.value)
                        }
                      />
                      <Label check htmlFor="priceNormal">
                        Normal Fiyat
                      </Label>
                    </FormGroup>

                    <FormGroup check style={{ width: "105px" }}>
                      <Input
                        name="priceModification"
                        type="radio"
                        id="priceDecrease"
                        value="decrease"
                        checked={priceModificationMode === "decrease"}
                        onChange={(e) =>
                          setPriceModificationMode(e.target.value)
                        }
                      />
                      <Label check htmlFor="priceDecrease">
                        Fiyat Azalt
                      </Label>
                    </FormGroup>

                    <FormGroup check style={{ width: "105px" }}>
                      <Input
                        name="priceModification"
                        type="radio"
                        id="priceIncrease"
                        value="increase"
                        checked={priceModificationMode === "increase"}
                        onChange={(e) =>
                          setPriceModificationMode(e.target.value)
                        }
                      />
                      <Label check htmlFor="priceIncrease">
                        Fiyat Arttır
                      </Label>
                    </FormGroup>
                  </div>

                  {priceModificationMode === "decrease" && (
                    <div className="mt-4">
                      <Label htmlFor="discountRange" className="form-label">
                        Komisyon Kullanım Oranı: %{discountPercentage}
                      </Label>
                      <Input
                        id="discountRange"
                        type="range"
                        className="form-range"
                        min="0"
                        max="100"
                        step="5"
                        value={discountPercentage}
                        onChange={(e) =>
                          setDiscountPercentage(parseFloat(e.target.value))
                        }
                      />
                      <div className="text-muted fs-12 mt-1">
                        Gerçekleşen İndirim: %
                        {((discountPercentage * commissionRate) / 100).toFixed(
                          2,
                        )}
                      </div>
                    </div>
                  )}

                  {priceModificationMode === "increase" && (
                    <div className="mt-4">
                      <Label htmlFor="increaseAmount" className="form-label">
                        Eklenecek Tutar (TL)
                      </Label>
                      <Input
                        id="increaseAmount"
                        name="increaseAmount"
                        type="number"
                        placeholder="Örn: 100"
                        min="0"
                        value={increaseAmount}
                        onChange={(e) => setIncreaseAmount(e.target.value)}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          <Card>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <div className="flex-grow-1">
                  <h5 className="card-title mb-0">Paket Detayları</h5>
                </div>
              </div>

              <div className="mb-4">
                <div className="table-responsive">
                  <table className="table table-borderless table-sm mb-0">
                    <tbody>
                      {packageState.IsBulkSale && (
                        <tr>
                          <th className="ps-0" scope="row">
                            Kalan Paket
                          </th>
                          <td className="text-end text-muted">
                            {bulkPackageCount} Adet
                          </td>
                        </tr>
                      )}
                      {[
                        {
                          val: packageState.TotalLimit,
                          label: "Yıllık Tutar Limiti",
                          desc: packageState.TotalLimitDesc,
                        },
                        {
                          val: packageState.Accident,
                          label: "Kaza Hakkı",
                          desc: packageState.AccidentDesc,
                        },
                        {
                          val: packageState.Fault,
                          label: "Arıza Hakkı",
                          desc: packageState.FaultDesc,
                        },
                        {
                          val: packageState.Tire,
                          label: "Lastik Patlaması",
                          desc: packageState.TireDesc,
                        },
                        {
                          val: packageState.Fuel,
                          label: "Yakıt Bitmesi",
                          desc: packageState.FuelDesc,
                        },
                        {
                          val: packageState.Windshield,
                          label: "Ön Cam",
                          desc: packageState.WindshieldDesc,
                        },
                        {
                          val: packageState.Glass,
                          label: "Cam Kırılması",
                          desc: packageState.GlassDesc,
                        },
                        {
                          val: packageState.Ambulance,
                          label: "Ambulans",
                          desc: packageState.AmbulanceDesc,
                        },
                        {
                          val: packageState.FireDepartment,
                          label: "İtfaiye",
                          desc: packageState.FireDepartmentDesc,
                        },
                        {
                          val: packageState.Locksmith,
                          label: "Çilingir",
                          desc: packageState.LocksmithDesc,
                        },
                        {
                          val: packageState.Landslide,
                          label: "Heyelan",
                          desc: packageState.LandslideDesc,
                        },
                        {
                          val: packageState.Headlight,
                          label: "Far Arıza",
                          desc: packageState.HeadlightDesc,
                        },
                        {
                          val: packageState.Health,
                          label: "Sağlık Sorunu",
                          desc: packageState.HealthDesc,
                        },
                        {
                          val: packageState.Tired,
                          label: "Yorgunluk Asistanı",
                          desc: packageState.TiredDesc,
                        },
                        {
                          val: packageState.EVFault,
                          label: "Elektrikli Araç",
                          desc: packageState.EVFaultDesc,
                        },
                        {
                          val: packageState.WildAnimal,
                          label: "Yabani Hayvan",
                          desc: packageState.WildAnimalDesc,
                        },
                      ].map(
                        (item, index) =>
                          item.val > 0 && (
                            <tr
                              key={index}
                              className="border-bottom border-bottom-dashed"
                            >
                              <th className="ps-0 py-3" scope="row">
                                <span className="text-body">{item.label}</span>
                                <p className="text-muted mb-0 fs-11 fw-normal">
                                  {item.desc}
                                </p>
                              </th>
                              <td className="text-end py-3">
                                <span className="fw-medium">{item.val}</span>
                              </td>
                            </tr>
                          ),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 pt-2">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-body">Ana Fiyat</span>
                  <span className="fw-semibold">
                    {formatCurrency(basePrice)} TL
                  </span>
                </div>

                {showCommissionFeatures &&
                  priceModificationMode !== "normal" && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-body"> {modificationLabel}</span>
                      <span className="fw-semibold">
                        {formatCurrency(Math.abs(modificationAmount))} TL
                      </span>
                    </div>
                  )}

                <div className="d-flex justify-content-between mb-2">
                  <span className="text-body">Toplam Tutar</span>
                  <span className="fw-semibold">
                    {formatCurrency(finalPrice)} TL
                  </span>
                </div>

                {showCommissionFeatures && (
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-body">Komisyon Kazancı</span>
                    <span className="fw-semibold">
                      {formatCurrency(totalEarnings)} TL
                    </span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default Checkout;
